/**
 * `noy-db add <collection>` — scaffold a new collection inside an existing
 * Nuxt 4 project that already has `@noy-db/in-nuxt` configured.
 *
 * The command writes two files:
 *
 *   1. `app/stores/<collection>.ts` — a `defineNoydbStore<T>()` call with
 *      a placeholder `T` interface and one example field. The user fills
 *      in the real shape after the file is created.
 *
 *   2. `app/pages/<collection>.vue` — a minimal CRUD page that lists,
 *      adds, and deletes records. The store ID and collection name are
 *      derived from the argument; everything else is boilerplate.
 *
 * The command refuses to overwrite existing files. If either target
 * already exists it logs which one and exits non-zero — the user has to
 * delete or move the file first. There's no `--force` because forcing an
 * overwrite of generated UI code is almost always a footgun in disguise.
 */

import { promises as fs } from 'node:fs'
import path from 'node:path'

export interface AddCollectionOptions {
  /** The collection name. Must be a lowercase identifier. */
  name: string
  /** Project root. Defaults to `process.cwd()`. */
  cwd?: string
  /** Compartment id to embed in the generated store. Defaults to `default`. */
  vault?: string
}

/**
 * Result returned to callers (the bin entry uses this to format output;
 * tests assert on the file paths).
 */
export interface AddCollectionResult {
  /** Files written, in the order they were created. */
  files: string[]
}

/**
 * Lowercase identifier check, narrower than the project-name check —
 * collection names become Vue component names, Pinia store IDs, AND TS
 * symbols, so we keep them simple: letters, digits, hyphens, must start
 * with a letter.
 */
export function validateCollectionName(name: string): string | null {
  if (!name) return 'Collection name is required'
  if (!/^[a-z][a-z0-9-]*$/.test(name)) {
    return 'Collection name must start with a lowercase letter and contain only lowercase letters, digits, or hyphens'
  }
  return null
}

export async function addCollection(
  options: AddCollectionOptions,
): Promise<AddCollectionResult> {
  const err = validateCollectionName(options.name)
  if (err) throw new Error(err)

  const cwd = options.cwd ?? process.cwd()
  const vault = options.vault ?? 'default'
  const name = options.name

  // Convert kebab-case to PascalCase for the TS interface name and the
  // Vue helper variable.
  const PascalName = name
    .split('-')
    .map((s) => (s.length === 0 ? s : (s[0]?.toUpperCase() ?? '') + s.slice(1)))
    .join('')
  const useFnName = `use${PascalName}`

  const storePath = path.join(cwd, 'app', 'stores', `${name}.ts`)
  const pagePath = path.join(cwd, 'app', 'pages', `${name}.vue`)

  // Refuse to overwrite. Doing both checks before writing either file
  // means a partial write is impossible — either both files land or
  // neither does.
  for (const target of [storePath, pagePath]) {
    if (await pathExists(target)) {
      throw new Error(`Refusing to overwrite existing file: ${target}`)
    }
  }

  await fs.mkdir(path.dirname(storePath), { recursive: true })
  await fs.mkdir(path.dirname(pagePath), { recursive: true })

  await fs.writeFile(storePath, renderStore(name, PascalName, useFnName, vault), 'utf8')
  await fs.writeFile(pagePath, renderPage(name, PascalName, useFnName), 'utf8')

  return { files: [storePath, pagePath] }
}

async function pathExists(p: string): Promise<boolean> {
  try {
    await fs.access(p)
    return true
  } catch {
    return false
  }
}

/**
 * Renders the `app/stores/<name>.ts` file. The interface is intentionally
 * minimal — `id` and `name` are the only fields. The user is expected to
 * extend it after generation; we'd rather not lock them into a domain
 * shape we guessed.
 */
function renderStore(
  name: string,
  PascalName: string,
  useFnName: string,
  vault: string,
): string {
  return `// Generated by \`noy-db add ${name}\`.
//
// Edit the ${PascalName} interface to match your domain, then call
// \`${useFnName}()\` from any component.
//
// defineNoydbStore is auto-imported by @noy-db/in-nuxt. The vault
// id is the tenant/company namespace — change it if you have multiple.

export interface ${PascalName} {
  id: string
  name: string
}

export const ${useFnName} = defineNoydbStore<${PascalName}>('${name}', {
  vault: '${vault}',
})
`
}

/**
 * Renders the `app/pages/<name>.vue` page — list + add + delete. Picks
 * the simplest possible template-renderer pattern (`v-for`, no fancy
 * components) so the generated file is easy to read and modify.
 */
function renderPage(name: string, PascalName: string, useFnName: string): string {
  return `<!--
  Generated by \`noy-db add ${name}\`.
  Visit /${name} in your dev server.
-->
<script setup lang="ts">
const ${name} = ${useFnName}()
await ${name}.$ready

function addOne() {
  ${name}.add({
    id: crypto.randomUUID(),
    name: 'New ${PascalName}',
  })
}

function removeOne(id: string) {
  ${name}.remove(id)
}
</script>

<template>
  <main>
    <h1>${PascalName}</h1>
    <button @click="addOne">Add ${PascalName}</button>
    <ul>
      <li v-for="item in ${name}.items" :key="item.id">
        {{ item.name }}
        <button @click="removeOne(item.id)">Delete</button>
      </li>
    </ul>
  </main>
</template>
`
}
